import Reveal from "@/components/Reveal";
import { Target, PenTool, Rocket, SearchCheck, ArrowUpRight } from "lucide-react";
import site from "@/config/site";

const ChapterHead = ({ num, title }) => (
  <div className="flex items-baseline gap-4 mb-6">
    <span className="font-mono2 text-xs tracking-[0.3em] text-[var(--accent)]">{num}</span>
    <span className="h-px flex-1 bg-[#27331C]" />
    <span className="font-mono2 text-xs tracking-[0.3em] text-[#8A8C7E]">{title}</span>
  </div>
);

const Chapters = () => (
  <section data-testid="manifesto-section" className="relative max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-36 space-y-28 sm:space-y-40">
    {/* 01 — Problem */}
    <div className="grid lg:grid-cols-2 gap-10 items-start">
      <Reveal>
        <ChapterHead num="01" title="THE PROBLEM" />
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-[#F2F1E8]">
          You're working hard.<br />
          <span className="italic font-light text-[var(--accent)]">Your brand isn't.</span>
        </h2>
      </Reveal>
      <Reveal delay={0.15} className="lg:pt-16">
        <ul className="space-y-4 text-sm sm:text-base text-[#B9BBAC] leading-relaxed">
          {[
            "Posting every day, but enquiries are random — if they come at all.",
            "A website that looks fine but quietly fails to convert visitors.",
            "Money going into boosted posts with no funnel behind them.",
            "Competitors with weaker products winning because they look sharper.",
          ].map((t, i) => (
            <li key={i} className="flex gap-4">
              <span className="font-mono2 text-[var(--accent)] text-xs pt-1">0{i + 1}</span>
              <span>{t}</span>
            </li>
          ))}
        </ul>
        <p className="mt-6 font-mono2 text-xs tracking-[0.2em] text-[#E4B54A]">SOUND FAMILIAR? THAT'S THE LEAK.</p>
      </Reveal>
    </div>

    {/* 02 — Method */}
    <div>
      <Reveal>
        <ChapterHead num="02" title="THE METHOD" />
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-[#F2F1E8] max-w-3xl">
          Audit first. <span className="italic font-light text-[var(--accent)]">Then strategy.</span><br />
          Then execution that compounds.
        </h2>
      </Reveal>
      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { icon: SearchCheck, t: "Audit", d: "We find every leak — positioning, website, content, funnel, numbers." },
          { icon: Target, t: "Strategy", d: "A sharp plan built around your market, your audience, your margins." },
          { icon: PenTool, t: "Roadmap", d: "A 90-day, step-by-step path — what to do, in what order, and why." },
          { icon: Rocket, t: "Execution", d: "We build it with you — brand, website, content, ads. End to end." },
        ].map((s, i) => (
          <Reveal key={s.t} delay={i * 0.08}>
            <div data-testid={`method-step-${i}`} className="hover-card-glow group h-full rounded-2xl border border-[#27331C] bg-[#0E140A] p-6">
              <s.icon className="w-6 h-6 text-[var(--accent)] mb-5" />
              <h3 className="font-display text-2xl text-[#F2F1E8] mb-2">{s.t}</h3>
              <p className="text-sm text-[#8A8C7E] leading-relaxed">{s.d}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>

    {/* 03 — Proof */}
    <div>
      <Reveal>
        <ChapterHead num="03" title="THE PROOF" />
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-[#F2F1E8] max-w-3xl">
          Proof over promises.<br />
          <span className="italic font-light text-[var(--accent)]">Numbers don't lie.</span>
        </h2>
      </Reveal>
      <div className="mt-12 grid sm:grid-cols-2 gap-4">
        {[
          { name: "Sanvalio", tag: "STRATEGY · DESIGN · GROWTH", url: site.social.sanvalio, testid: "proof-sanvalio" },
          { name: "Design4You", tag: "BRANDING · WEBSITES · DIGITAL", url: site.social.design4you, testid: "proof-design4you" },
        ].map((b, i) => (
          <Reveal key={b.name} delay={i * 0.1}>
            <a href={b.url} target="_blank" rel="noopener noreferrer" data-testid={b.testid}
              className="group block rounded-2xl border border-[#27331C] bg-[#0E140A] px-7 py-7 hover:border-[var(--accent)]/60 transition-colors duration-500">
              <div className="flex items-center justify-between gap-4">
                <span className="font-display text-2xl sm:text-3xl text-[#F2F1E8]">{b.name}<span className="text-[var(--accent)]">.</span></span>
                <ArrowUpRight className="w-5 h-5 shrink-0 text-[#8A8C7E] group-hover:text-[var(--accent)] transition-colors duration-300" />
              </div>
              <p className="mt-2.5 font-mono2 text-[10px] tracking-[0.22em] text-[#8A8C7E] group-hover:text-[var(--accent)] transition-colors duration-300">{b.tag}</p>
            </a>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default Chapters;
